import React, { useRef, useState, useEffect } from 'react';
import './SkiGame.css';

const SkiGame = () => {
  // Get viewport dimensions (assumes mobile portrait mode)
  const gameWidth = window.innerWidth;
  const gameHeight = window.innerHeight;

  // Define sizes for the skier (in pixels)
  const skierWidth = 30;
  const skierHeight = 30;

  // Game speed factors
  const speed = 3; // obstacle vertical speed factor
  const turnSpeed = 0.3; // multiplier for how fast the skier moves horizontally based on touch

  // Use refs to hold the game state so that the animation loop can update them without re-creating effects
  const skierRef = useRef({
    // Start the skier near the bottom-center of the screen.
    x: gameWidth / 2 - skierWidth / 2,
    y: gameHeight - skierHeight - 20,
    angle: 0, // controlled by touch-drag; used to adjust horizontal movement
  });

  // Create an initial array of obstacles.
  // In this example each obstacle is simply a circle (you can later style these as trees or rocks)
  const initialObstacles = [];
  const obstacleCount = 5;
  for (let i = 0; i < obstacleCount; i++) {
    const obstacleWidth = 40;
    const obstacleHeight = 40;
    const x = Math.random() * (gameWidth - obstacleWidth);
    const y = -Math.random() * gameHeight; // start them at random positions above the viewport
    initialObstacles.push({ x, y, width: obstacleWidth, height: obstacleHeight, id: i });
  }
  const obstaclesRef = useRef(initialObstacles);

  // This state is only used to trigger a re-render on every animation frame.
  const [tick, setTick] = useState(0);

  // For touch controls, we record the initial touch position.
  const touchStartRef = useRef(null);

  // The game loop – update positions and check for collisions using requestAnimationFrame.
  useEffect(() => {
    let animationFrameId;
    let lastTime = performance.now();

    const update = (time) => {
      const deltaTime = time - lastTime;
      lastTime = time;

      // Update the skier’s horizontal position based on its current angle.
      const skier = skierRef.current;
      let newX = skier.x + skier.angle * turnSpeed * (deltaTime / 16); // normalize deltaTime assuming 16ms frame
      // Keep the skier within the viewport
      newX = Math.max(0, Math.min(newX, gameWidth - skierWidth));
      skier.x = newX;

      // Move each obstacle downward (giving the effect that the skier is moving downhill)
      const obstacles = obstaclesRef.current;
      for (let obs of obstacles) {
        obs.y += speed * (deltaTime / 16);
        // If an obstacle has moved below the bottom, recycle it back to the top with a new random x coordinate.
        if (obs.y > gameHeight) {
          obs.y = -obs.height;
          obs.x = Math.random() * (gameWidth - obs.width);
        }
      }

      // Check for collisions (simple AABB collision detection)
      let collision = false;
      for (let obs of obstacles) {
        if (
          skier.x < obs.x + obs.width &&
          skier.x + skierWidth > obs.x &&
          skier.y < obs.y + obs.height &&
          skier.y + skierHeight > obs.y
        ) {
          collision = true;
          break;
        }
      }
      if (collision) {
        alert('Game Over!');
        // Reset the skier to the starting position and clear obstacles.
        skier.x = gameWidth / 2 - skierWidth / 2;
        skier.angle = 0;
        for (let obs of obstacles) {
          obs.y = -Math.random() * gameHeight;
          obs.x = Math.random() * (gameWidth - obs.width);
        }
      }

      // Trigger a re-render.
      setTick(t => t + 1);
      animationFrameId = requestAnimationFrame(update);
    };

    animationFrameId = requestAnimationFrame(update);
    return () => cancelAnimationFrame(animationFrameId);
  }, [gameWidth, gameHeight, skierWidth, skierHeight, speed, turnSpeed]);

  // Handle touch start: record the initial touch position.
  const handleTouchStart = (e) => {
    const touch = e.touches[0];
    touchStartRef.current = { x: touch.clientX, y: touch.clientY };
  };

  // On touch move, compute how far the finger has moved horizontally and adjust the skier’s angle.
  const handleTouchMove = (e) => {
    e.preventDefault(); // Prevent scrolling on mobile.
    const touch = e.touches[0];
    if (touchStartRef.current) {
      const deltaX = touch.clientX - touchStartRef.current.x;
      // Convert the horizontal drag into an "angle" value.
      let newAngle = deltaX / 100; // Adjust sensitivity by changing the divisor.
      // Clamp the angle to a maximum left/right value.
      newAngle = Math.max(-2, Math.min(newAngle, 2));
      skierRef.current.angle = newAngle;
    }
  };

  // On touch end, reset the touch reference and the skier’s angle.
  const handleTouchEnd = () => {
    touchStartRef.current = null;
    skierRef.current.angle = 0;
  };

  return (
    <div 
      className="ski-game-container" 
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* The skier element */}
      <div 
        className="skier" 
        style={{
          left: `${skierRef.current.x}px`,
          top: `${skierRef.current.y}px`,
          // Rotate the skier to reflect the turning angle (multiplied to exaggerate the visual effect)
          transform: `rotate(${skierRef.current.angle * 15}deg)`,
          width: `${skierWidth}px`,
          height: `${skierHeight}px`,
        }}
      ></div>

      {/* Render each obstacle */}
      {obstaclesRef.current.map((obs) => (
        <div 
          key={obs.id} 
          className="obstacle" 
          style={{
            left: `${obs.x}px`,
            top: `${obs.y}px`,
            width: `${obs.width}px`,
            height: `${obs.height}px`,
          }}
        ></div>
      ))}
    </div>
  );
};

export default SkiGame;
